import type { PeerMetadata } from '@playcastdotio/Messaging';
import { MobileTierPolicy } from './mobileTier/MobileTierPolicy';
import { SenderResolutionCap } from './mobileTier/SenderResolutionCap';
import { SenderCodecPin } from './SenderCodecPin';
import type { SenderRecoveryLimit } from './mediaPolicy/PeerMediaPolicyLifecycle';
import type { SdpBitrateConfig } from './SdpBitrateTransform';
import {
  MOBILE_AUDIO_DEGRADATION,
  MOBILE_AUDIO_MAX_BITRATE_BPS,
  STARTUP_BURST_MS,
  TARGET_FRAMERATE,
  AUDIO_JITTER_BUFFER_TARGET_MS,
  VIDEO_JITTER_BUFFER_TARGET_MS,
  bitrateProfileFor,
  classifyImplementationName,
} from './webrtcLatencyContracts';
import type {
  HardwareAccelerationKind,
  StreamResolution,
} from './webrtcLatencyContracts';
import {
  DEFAULT_HW_PROBE_VIDEO,
  codecMatchesPreferred,
  type HardwareProbeVideoTarget,
} from './preferHardwareVideoCodecs';
import { steadySendBandBps } from './streamBitrateModel';
import {
  describeNegotiatedCodec,
  restrictToHardwareVideoCodecs,
  type EncodeProfileProbe,
  type SendCodecDecision,
} from './hardwareCodecPolicy';
import { videoQualityPolicy, type VideoPlatform } from './mediaPolicy/VideoQualityPolicy';

type CodecCapability = {
  mimeType: string;
  clockRate: number;
  channels?: number;
  sdpFmtpLine?: string;
};

export { classifyImplementationName };
export type { HardwareAccelerationKind };

export type WebRtcOptimizationConfig = {
  codecPreferences?: { video?: string[]; audio?: string[] };
  /** Steady-state (settle) max bitrate in bps. */
  maxBitrate?: number;
  /** Steady-state SDP bitrate hints (kbps). */
  sdpBitrate?: SdpBitrateConfig;
  /** Startup burst max bitrate in bps — applied by beginStartupBurstThenSettle. */
  burstMaxBitrate?: number;
  /** Startup burst SDP bitrate hints (kbps) for initial offer transform. */
  burstSdpBitrate?: SdpBitrateConfig;
  audioMaxBitrate?: number;
  /** When false, the Opus offer drops the stereo/sprop-stereo fmtp (mobile degradation). */
  audioStereo?: boolean;
  targetFrameRate?: number;
  /** When true, video encodings[0].scalabilityMode is set to L1T3 (temporal layers). */
  temporalScalability?: boolean;
  degradationPreference?: 'maintain-framerate' | 'maintain-resolution' | 'balanced';
  lowLatencyMode?: boolean;
  jitterBufferTarget?: number;
  /** Receiver minimum jitter target for audio tracks (ms). Video uses jitterBufferTarget. */
  audioJitterBufferTarget?: number;
  /**
   * True for Apple guests (from metadata.isApple). Playout and other Apple-only paths
   * read this.
   */
  isAppleGuest?: boolean;
  /**
   * Pinned session resolution, used to ask the hardware-capability probe about the
   * encode/decode this peer will actually run rather than a fixed 720p guess.
   */
  captureResolution?: StreamResolution;
  /**
   * Video families the GUEST decodes in HARDWARE, straight from
   * PeerMetadata.hardwareDecodeFamilies via configFromMetadata.
   *
   * Carried on the config rather than passed at the pin call site so that every path which
   * reaches pinSenderToHardwareCodec — connect, after-answer reprime, the sustained-software
   * retry — gets it without having to remember to. Absent means UNKNOWN: the send-codec
   * choice stays on host-only probing for H.264. HEVC requires an affirmative guest report.
   */
  guestHardwareDecodeFamilies?: string[];
};

/**
 * navigator.mediaCapabilities.encodingInfo narrowed to the codec-pin question, or null when
 * the API is unavailable (in which case pinning is skipped entirely).
 *
 * Probes at the SESSION'S real encode target rather than a fixed 720p guess — a host whose
 * hardware support varies with resolution must be asked about the resolution it will
 * actually encode.
 */
function encodeProfileProbe(target: HardwareProbeVideoTarget): EncodeProfileProbe | null {
  const mediaCapabilities = globalThis.navigator?.mediaCapabilities;
  const encodingInfo = (
    mediaCapabilities as (MediaCapabilities & {
      encodingInfo?: (config: unknown) => Promise<{ supported?: boolean; powerEfficient?: boolean }>;
    }) | undefined
  )?.encodingInfo;
  if (!mediaCapabilities || !encodingInfo) return null;

  return (contentType) =>
    encodingInfo.call(mediaCapabilities, {
      type: 'webrtc',
      video: {
        contentType,
        width: target.width,
        height: target.height,
        bitrate: target.bitrate,
        framerate: target.framerate,
      },
      // Non-standard Chromium hint; it is what makes Chromium answer for the hardware path.
      hardwareAcceleration: 'prefer-hardware',
    });
}

export type SdpTransformPathOptions = {
  /**
   * Chromium low-latency path. False yields the video bitrate hints alone when
   * `audioOpusPolicy` is also omitted. Defaults to true.
   */
  lowLatencyPlayout?: boolean;
  /**
   * Rewrite Opus to 20 ms, no DTX, no in-band FEC. Set independently of
   * `lowLatencyPlayout` so Safari can take the audio policy without moving video.
   * When omitted, follows `lowLatencyPlayout`.
   */
  audioOpusPolicy?: boolean;
};

/**
 * SDP transform config from optimization settings (video bitrate hints + audio Opus policy).
 * Pass `videoBitrateHints` when the session phase uses settle hints instead of burst.
 */
export function sdpTransformConfigFromOptimization(
  config: WebRtcOptimizationConfig,
  videoBitrateHints?: SdpBitrateConfig,
  path: SdpTransformPathOptions = {},
): SdpBitrateConfig | undefined {
  const base = videoBitrateHints ?? config.burstSdpBitrate ?? config.sdpBitrate;
  if (!base) return undefined;
  const audioOpusPolicy = path.audioOpusPolicy ?? path.lowLatencyPlayout !== false;
  if (!audioOpusPolicy) return { ...base, audioOpusPolicy: false };
  return {
    ...base,
    audioMaxBitrate: config.audioMaxBitrate,
    audioStereo: config.audioStereo,
    audioOpusPolicy: true,
  };
}

/** Convert bps values to SDP kbps hints for createSdpBitrateTransform. */
export function sdpBitrateFromBps(maxBps: number, startBps: number, minBps: number): SdpBitrateConfig {
  const toKbps = (bps: number) => Math.max(1, Math.round(bps / 1000));
  return {
    maxBitrate: toKbps(maxBps),
    startBitrate: toKbps(startBps),
    minBitrate: toKbps(minBps),
  };
}

/** Client → host beacon datachannel: `{"type":"requestKeyframe"}`. */
export type RequestKeyframeMessage = {
  type: 'requestKeyframe';
};

export const REQUEST_KEYFRAME_MESSAGE: RequestKeyframeMessage = {
  type: 'requestKeyframe',
};

/**
 * Derives WebRtcOptimizationConfig from peer metadata.
 * Steady fields use the settle profile; burst fields feed beginStartupBurstThenSettle.
 * Every guest platform receives the same HEVC-then-H.264 codec order.
 *
 * captureResolution uses MobileTierPolicy so the hardware-capability probe
 * matches the ShadowCam the session actually uses (respecting connectionQuestionable).
 *
 * guestHardwareDecodeFamilies is carried through verbatim. Missing and empty evidence both
 * fail closed when the sender codec is selected.
 */
export function configFromMetadata(metadata: PeerMetadata): WebRtcOptimizationConfig {
  const isMobile = metadata.isMobile !== false;
  const isFirefox = metadata.isFirefox ?? false;
  const isApple = metadata.isApple === true;
  // Confirmed Android Chrome, not "isMobile missing" and not Firefox.
  const isAndroid = metadata.isMobile === true && !isApple && !isFirefox;
  // Confirmed desktop Chromium. Missing isMobile stays on H.264; Firefox keeps its own order.
  const platform: VideoPlatform = isApple
    ? 'safari'
    : isAndroid
      ? 'android-chromium'
      : isFirefox
        ? 'firefox'
        : metadata.isMobile === false
          ? 'desktop-chromium'
          : 'unknown';
  const videoCodecOrder = ['h265', 'h264'];

  const settle = bitrateProfileFor(isMobile, 'settle');
  const burst = bitrateProfileFor(isMobile, 'burst');

  const tierSelection = new MobileTierPolicy().select({
    device: metadata.isMobile === true ? 'mobile' : metadata.isMobile === false ? 'desktop' : 'unknown',
    connectionQuestionable: metadata.connectionQuestionable,
  });
  // Codec is not pinned yet. H.264 is the conservative assumption: it asks for more bits
  // than HEVC, and the pin tightens the sender cap once the real codec is known.
  const band = steadySendBandBps(settle.maxBitrate, {
    resolution: { width: tierSelection.maxWidth, height: tierSelection.maxHeight },
    frameRate: TARGET_FRAMERATE,
    codec: 'h264',
    platform,
    encoder: 'unknown',
    captureMode: 'display',
  });
  const hints = (startBps: number) => {
    const start = Math.min(Math.max(startBps, band.minBitrateBps), band.maxBitrateBps);
    return sdpBitrateFromBps(band.maxBitrateBps, start, band.minBitrateBps);
  };

  return {
    codecPreferences: { video: videoCodecOrder, audio: ['opus'] },
    maxBitrate: band.maxBitrateBps,
    sdpBitrate: hints(settle.startBitrate),
    // No startup burst above the picture. A 6–10 Mbps ceiling on a 480p frame is the
    // budget the encoder banks, and the first SDP ceiling is the one the session keeps.
    burstMaxBitrate: band.maxBitrateBps,
    burstSdpBitrate: hints(burst.startBitrate),
    audioMaxBitrate: MOBILE_AUDIO_DEGRADATION && isMobile
      ? MOBILE_AUDIO_MAX_BITRATE_BPS
      : 128_000,
    audioStereo: !(MOBILE_AUDIO_DEGRADATION && isMobile),
    targetFrameRate: TARGET_FRAMERATE,
    // PRODUCT DECISION - currently OFF. L1T3 helps low-latency degradation once the
    // encoder fleet supports it; leave the knob explicit so it is discoverable and safe.
    temporalScalability: false,
    degradationPreference: videoQualityPolicy.degradationPreferenceFor(platform),
    lowLatencyMode: true,
    jitterBufferTarget: VIDEO_JITTER_BUFFER_TARGET_MS,
    // Audio NetEq floor for every guest, including Safari. separateAudioMsid keeps this
    // depth off the video sync group, so it does not pull video out of render-ASAP.
    // The value is a floor: jitterBufferTarget can only raise the minimum.
    audioJitterBufferTarget: metadata.isFirefox === true ? 0 : AUDIO_JITTER_BUFFER_TARGET_MS,
    isAppleGuest: metadata.isApple === true,
    captureResolution: { width: tierSelection.maxWidth, height: tierSelection.maxHeight },
    ...(metadata.hardwareDecodeFamilies !== undefined
      ? { guestHardwareDecodeFamilies: metadata.hardwareDecodeFamilies }
      : {}),
  };
}

/**
 * Probe target for the hardware-capability check: resolution from the pinned session
 * profile, bitrate and framerate from the encode this peer is configured for.
 */
export function hardwareProbeVideoFor(
  config: WebRtcOptimizationConfig,
): HardwareProbeVideoTarget {
  return {
    width: config.captureResolution?.width ?? DEFAULT_HW_PROBE_VIDEO.width,
    height: config.captureResolution?.height ?? DEFAULT_HW_PROBE_VIDEO.height,
    bitrate: config.maxBitrate ?? DEFAULT_HW_PROBE_VIDEO.bitrate,
    framerate: config.targetFrameRate ?? DEFAULT_HW_PROBE_VIDEO.framerate,
  };
}

/**
 * Applies WebRTC optimizations: codec prefs (awaiting preferHardware),
 * sender bitrate/fps/degradation, and receiver low-latency hints.
 * Null RTCPeerConnection is a no-op (test mocks).
 */
export class WebRtcOptimizationManager {
  private readonly senderResolutionCap = new SenderResolutionCap();
  private readonly senderCodecPin = new SenderCodecPin();

  /** A pin owns its sender through probing and activation; later writes read fresh parameters. */
  private readonly senderParameterUpdates = new WeakMap<RTCRtpSender, Promise<unknown>>();

  private serializeSenderParameters<T>(sender: RTCRtpSender, update: () => Promise<T>): Promise<T> {
    const previous = this.senderParameterUpdates.get(sender);
    const pending = previous ? previous.then(update, update) : update();
    const settled = pending.finally(() => {
      if (this.senderParameterUpdates.get(sender) === settled) this.senderParameterUpdates.delete(sender);
    });
    this.senderParameterUpdates.set(sender, settled);
    return settled;
  }

  public enforceSenderResolution(rtcPc: RTCPeerConnection, limit: StreamResolution): Promise<void> {
    return this.senderResolutionCap.enforce(rtcPc, limit);
  }
  private static readonly ENCODING_RETRY_DELAY_MS = 150;
  private pendingEncodingRetry = new WeakSet<RTCRtpSender>();
  private settleTimers = new WeakMap<RTCPeerConnection, ReturnType<typeof setTimeout>>();
  /**
   * The WeakMap above stops this manager retaining peer connections, but a WeakMap cannot
   * be enumerated and dropping a reference never cancels a scheduled callback. Pending
   * timers are tracked separately so teardown can actually clear them.
   */
  private pendingSettleTimers = new Set<ReturnType<typeof setTimeout>>();
  /** Same reasoning as pendingSettleTimers, for the empty-encodings retry. */
  private pendingEncodingRetryTimers = new Set<ReturnType<typeof setTimeout>>();

  /**
   * Pre-connect codec preferences. Resolves only after preferHardware finishes.
   */
  async applyPreConnectionOptimizations(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
  ): Promise<void> {
    if (!rtcPc) return;

    try {
      await this.senderResolutionCap.enforce(rtcPc, config.captureResolution);
    } catch (error) {
      // A mismatched capture must not scale into a new size, and it must not
      // skip codec setup. The sender keeps the capture it already has.
      console.warn('[WebRtcOptimizationManager] Capture profile was not enforced:', error);
    }

    try {
      await this.applyCodecPreferences(rtcPc, config);
    } catch (error) {
      console.warn('[WebRtcOptimizationManager] Pre-connection optimization failed:', error);
    }
  }

  /**
   * Applies burst max bitrate immediately; after STARTUP_BURST_MS applies settle
   * via setParameters. Callers may also use burstSdpBitrate for the initial SDP transform.
   * @param skipSettleCheck Optional callback; if returns true, settle is skipped (e.g. recovery active).
   */
  beginStartupBurstThenSettle(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
    skipSettleCheck?: () => boolean,
  ): void {
    if (!rtcPc) return;

    this.cancelStartupBurstSettle(rtcPc);

    const burstMax = config.burstMaxBitrate ?? config.maxBitrate;
    if (burstMax && Number.isFinite(burstMax) && burstMax > 0) {
      this.applySenderParameters(rtcPc, { ...config, maxBitrate: burstMax }, skipSettleCheck);
    }

    const timer = setTimeout(() => {
      this.settleTimers.delete(rtcPc);
      this.pendingSettleTimers.delete(timer);
      if (skipSettleCheck?.()) return;
      this.applySenderParameters(rtcPc, config, skipSettleCheck);
    }, STARTUP_BURST_MS);
    this.settleTimers.set(rtcPc, timer);
    this.pendingSettleTimers.add(timer);
  }

  /** Cancel a pending settle for one peer connection, e.g. when that peer is torn down. */
  cancelStartupBurstSettle(rtcPc: RTCPeerConnection | null): void {
    if (!rtcPc) return;
    const prior = this.settleTimers.get(rtcPc);
    if (prior === undefined) return;
    clearTimeout(prior);
    this.settleTimers.delete(rtcPc);
    this.pendingSettleTimers.delete(prior);
  }

  /**
   * Cancel every pending settle and encoding retry. Without this, a torn-down session
   * keeps the event loop alive for the remainder of the burst window and fires
   * setParameters on a dead peer connection.
   */
  dispose(): void {
    for (const timer of this.pendingSettleTimers) {
      clearTimeout(timer);
    }
    this.pendingSettleTimers.clear();
    for (const timer of this.pendingEncodingRetryTimers) {
      clearTimeout(timer);
    }
    this.pendingEncodingRetryTimers.clear();
  }

  applyPostConnectionOptimizations(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
  ): void {
    if (!rtcPc) return;

    try {
      this.applySenderParameters(rtcPc, config);
      if (config.lowLatencyMode !== false) {
        this.applyReceiverLowLatencySettings(rtcPc, config);
      }
    } catch (error) {
      console.warn('[WebRtcOptimizationManager] Post-connection optimization failed:', error);
    }
  }

  private async applyCodecPreferences(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
  ): Promise<void> {
    const transceivers = rtcPc.getTransceivers();
    const pending: Promise<void>[] = [];

    for (const transceiver of transceivers) {
      const kind = this.resolveTransceiverKind(transceiver);
      if (!kind) continue;

      try {
        if (kind === 'video' && config.codecPreferences?.video) {
          pending.push(
            this.setCodecPreferencesForKindAsync(
              transceiver,
              'video',
              config.codecPreferences.video,
              config,
            ),
          );
        } else if (kind === 'audio' && config.codecPreferences?.audio) {
          this.setCodecPreferencesForKind(
            transceiver,
            'audio',
            config.codecPreferences.audio,
            config,
          );
        }
      } catch (error) {
        console.warn(`[WebRtcOptimizationManager] Failed to set ${kind} codec preferences:`, error);
      }
    }

    await Promise.all(pending);
  }

  private async setCodecPreferencesForKindAsync(
    transceiver: RTCRtpTransceiver,
    kind: 'video' | 'audio',
    preferredOrder: string[],
    config: WebRtcOptimizationConfig,
  ): Promise<void> {
    const hasSenderTrack = !!transceiver.sender?.track;
    if (kind === 'video' && hasSenderTrack) {
      // ANSWERER/SENDER (the host): apply NO video codec preferences at all.
      //
      // Three measured reasons. (1) An answerer's setCodecPreferences does not change what
      // it SENDS — the send codec follows the OFFER's order (RFC 3264), verified in a live
      // loopback where a 42001f-first answer still encoded 42e01f; the send codec is chosen
      // after negotiation by pinSenderToHardwareCodec instead. (2) Since the 2024 spec
      // change, setCodecPreferences validates against RECEIVE capabilities, so a list built
      // from sender capabilities can throw and silently void the whole pass. (3) Narrowing
      // the host's accept-set risks an empty codec intersection against a guest whose offer
      // has no overlapping family, which kills negotiation outright.
      return;
    }
    const capabilities = hasSenderTrack
      ? RTCRtpSender.getCapabilities(kind)
      : RTCRtpReceiver.getCapabilities(kind);
    if (!capabilities?.codecs) return;

    type Codec = (typeof capabilities.codecs)[number];
    let ordered: Codec[] = [];

    // Group codecs by the preferred family order, but DO NOT reorder profiles
    // within a family. Chromium lists H.264 profiles hardware-encoder-first;
    // preferHardwareVideoCodecs only reorders families, never profiles.
    for (const preferred of preferredOrder) {
      const matching = capabilities.codecs.filter(
        (c) => codecMatchesPreferred(c.mimeType, preferred) && !ordered.includes(c),
      );
      ordered.push(...matching);
    }

    const remaining = capabilities.codecs.filter((c) => !ordered.includes(c));
    ordered.push(...remaining);

    if (ordered.length === 0) return;

    if (kind === 'video') {
      // OFFERER/RECEIVER (the guest): its receive-preference order steers the host's send
      // codec, so shape it here. The restriction fails closed: when nothing qualifies,
      // `codecs` is empty and hardware is unavailable. Re-check emptiness AFTER the
      // restriction so an empty array is never handed to setCodecPreferences, and keep
      // refusing software codecs by leaving the hardware-qualified order out entirely.
      const policy = restrictToHardwareVideoCodecs(ordered);
      if (policy.codecs.length === 0) {
        return;
      }
      ordered = policy.codecs;
    }

    if (kind === 'audio') {
      const withFmtp = this.applyOpusStereoFmtp(ordered, config);
      try {
        transceiver.setCodecPreferences(withFmtp);
        return;
      } catch {
        // fall through
      }
    }

    transceiver.setCodecPreferences(ordered);
  }

  private resolveTransceiverKind(
    transceiver: RTCRtpTransceiver,
  ): 'audio' | 'video' | null {
    const kind =
      transceiver.sender?.track?.kind ??
      transceiver.receiver?.track?.kind ??
      null;

    if (kind === 'audio' || kind === 'video') return kind;
    return null;
  }

  private setCodecPreferencesForKind(
    transceiver: RTCRtpTransceiver,
    kind: 'video' | 'audio',
    preferredOrder: string[],
    config: WebRtcOptimizationConfig,
  ): void {
    const hasSenderTrack = !!transceiver.sender?.track;
    const capabilities = hasSenderTrack
      ? RTCRtpSender.getCapabilities(kind)
      : RTCRtpReceiver.getCapabilities(kind);
    if (!capabilities?.codecs) return;

    type Codec = (typeof capabilities.codecs)[number];
    const ordered: Codec[] = [];

    for (const preferred of preferredOrder) {
      const matching = capabilities.codecs.filter(
        (c) => codecMatchesPreferred(c.mimeType, preferred) && !ordered.includes(c),
      );
      ordered.push(...matching);
    }

    const remaining = capabilities.codecs.filter(
      (c) => !ordered.includes(c),
    );
    ordered.push(...remaining);

    if (ordered.length === 0) return;

    // For audio, try applying Opus stereo/bitrate fmtp. Chrome validates codec
    // objects against getCapabilities() and rejects modified sdpFmtpLine values.
    // When that happens, fall back to unmodified ordering — codec priority is
    // preserved and audio bitrate is still applied post-connection via
    // sender.setParameters().
    if (kind === 'audio') {
      const withFmtp = this.applyOpusStereoFmtp(ordered, config);
      try {
        transceiver.setCodecPreferences(withFmtp);
        return;
      } catch {
        // Modified codecs rejected; fall through to unmodified ordering
      }
    }

    transceiver.setCodecPreferences(ordered);
  }

  private applyOpusStereoFmtp(codecs: CodecCapability[], config: WebRtcOptimizationConfig): CodecCapability[] {
    const bitrate = config.audioMaxBitrate;
    const stereo = config.audioStereo !== false;
    return codecs.map((codec) => {
      if (!codec.mimeType.toLowerCase().includes('opus')) return codec;

      const existingFmtp = codec.sdpFmtpLine ?? '';
      const params = new Map<string, string>();
      if (existingFmtp) {
        for (const part of existingFmtp.split(';')) {
          const [key, val] = part.split('=');
          if (key) params.set(key.trim(), val?.trim() ?? '');
        }
      }

      if (stereo) {
        params.set('stereo', '1');
        params.set('sprop-stereo', '1');
      } else {
        // Explicit mono for the mobile degradation profile.
        params.set('stereo', '0');
        params.set('sprop-stereo', '0');
      }
      if (bitrate && Number.isFinite(bitrate) && bitrate > 0) {
        params.set('maxaveragebitrate', String(bitrate));
      }

      const fmtpLine = Array.from(params.entries())
        .map(([k, v]) => `${k}=${v}`)
        .join(';');

      return { ...codec, sdpFmtpLine: fmtpLine };
    });
  }

  /**
   * Apply sender parameters for bitrate, framerate, and encoding settings.
   * @param skipRetryCheck Optional callback; if returns true when a delayed encoding retry fires, skip re-applying (e.g. recovery active).
   */
  applySenderParameters(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
    skipRetryCheck?: () => boolean,
  ): void {
    const senders = rtcPc.getSenders();

    for (const sender of senders) {
      if (!sender.track) continue;

      if (sender.track.kind === 'video') {
        this.applyVideoSenderParams(sender, config, true, skipRetryCheck);
      } else if (sender.track.kind === 'audio') {
        this.applyAudioSenderParams(sender, config, true, skipRetryCheck);
      }
    }
  }

  private applyVideoSenderParams(
    sender: RTCRtpSender,
    config: WebRtcOptimizationConfig,
    allowRetry = true,
    skipRetryCheck?: () => boolean,
    recoveryLimit?: SenderRecoveryLimit,
  ): void {
    void this.serializeSenderParameters(sender, async () => {
      const params = sender.getParameters() as RTCRtpSendParameters;
      if (!params.encodings || params.encodings.length === 0) {
        this.scheduleEncodingRetryIfNeeded(sender, config, 'video', allowRetry, skipRetryCheck, recoveryLimit);
        return;
      }

      const encoding = params.encodings[0];

      const configuredBitrate = config.maxBitrate;
      const maxBitrate = recoveryLimit && configuredBitrate
        ? Math.min(recoveryLimit.maxBitrateBps, configuredBitrate)
        : recoveryLimit?.maxBitrateBps ?? configuredBitrate;
      if (maxBitrate && Number.isFinite(maxBitrate) && maxBitrate > 0) {
        encoding.maxBitrate = maxBitrate;
      }

      // Frame rate stays at 60. A lower targetFrameRate used to land here and the
      // encoder then discarded camera frames before encoding them.
      // const fps = config.targetFrameRate;
      // if (fps && Number.isFinite(fps) && fps > 0) {
      //   encoding.maxFramerate = fps;
      // }
      encoding.maxFramerate = recoveryLimit
        ? Math.min(recoveryLimit.maxFramerate, TARGET_FRAMERATE)
        : TARGET_FRAMERATE;

      // Temporal scalability (L1T3): one spatial layer, three temporal layers. With the
      // resolution pinned (maintain-resolution) and a fixed 60fps target, L1T3 is the
      // low-latency degradation lever that survives a congested link: the encoder can drop
      // temporal layers when bandwidth runs short instead of building a queue. Only applied
      // when the config opts in — WebKit rejects unknown scalabilityMode values.
      if (config.temporalScalability) {
        (encoding as { scalabilityMode?: string }).scalabilityMode = 'L1T3';
      }

      this.senderResolutionCap.remember(sender, config.captureResolution);
      // The capture profile is the frame size. A fractional scale lets OpenH264
      // emit a resolution nobody requested.
      encoding.scaleResolutionDownBy = 1;
      // Never force active=false. A missing probe or a rejected pin used to park the
      // sender off, and every later write repeated it: audio up, video black.
      encoding.active = true;
      // A6: do not set priority/networkPriority to 'high' — lets GCC back off.
      delete (encoding as { priority?: string }).priority;
      delete (encoding as { networkPriority?: string }).networkPriority;

      if (config.degradationPreference) {
        params.degradationPreference = config.degradationPreference;
      }

      await sender.setParameters(params);
    }).catch((err) => {
      console.warn('[WebRtcOptimizationManager] Failed to set video sender parameters:', err);
    });
  }

  /**
   * Select and pin the first video encoding's codec for each eligible sender using
   * the strict HEVC/H.264 hardware policy.
   *
   * A selected codec is written active. HEVC rejection tries mutually hardware-qualified
   * H.264 once. If neither pin is accepted, the returned failure must terminate the session.
   * Writes keep scaleResolutionDownBy at 1 and request maintain-resolution without renegotiating.
   *
   * setParameters failures are caught; pinAccepted distinguishes accepted writes from
   * failures. Errors reading sender parameters propagate.
   *
   * Safe to call repeatedly: a sender that already carries a pin KEEPS it. The pin is
   * sticky for the life of the sender because changing `encodings[0].codec` mid-stream
   * makes Chromium tear down and recreate the encoder. The sustained-software retry used to
   * re-run this every 10s; each run re-probes under a timeout race, so the chosen family
   * flipped and after a few flips the encoder came back dead — the preview blanked and
   * the guest rebuilt the peer about every 95s. Retrying still writes when NO pin has
   * landed yet.
   *
   * @returns The last selection attempted, or null if the connection is absent.
   *   An already-pinned sender returns that pin with `alreadyPinned` set and does not
   *   call setParameters. A sender with no encodings yet returns a retry-pending decision.
   */
  async pinSenderToHardwareCodec(
    rtcPc: RTCPeerConnection | null,
    config: WebRtcOptimizationConfig,
  ): Promise<SendCodecDecision<CodecCapability> | null> {
    if (!rtcPc || typeof rtcPc.getSenders !== 'function') return null;

    let lastDecision: SendCodecDecision<CodecCapability> | null = null;
    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      const decision = await this.serializeSenderParameters(sender, async () => {
        const params = sender.getParameters() as RTCRtpSendParameters & {
          codecs?: CodecCapability[];
        };
        const existingPin = (params.encodings?.[0] as { codec?: CodecCapability } | undefined)?.codec;
        if (existingPin) {
          const negotiated = params.codecs ?? [];
          return {
            codec: existingPin,
            alreadyPinned: true,
            considered: negotiated.map(describeNegotiatedCodec),
            hardware: [describeNegotiatedCodec(existingPin)],
            guestSoftwareDecode: [],
          };
        }
        return this.senderCodecPin.pin(
          sender,
          config,
          encodeProfileProbe(hardwareProbeVideoFor(config)),
        );
      });
      if (decision) lastDecision = decision;
    }
    return lastDecision;
  }

  /** Apply configured video sender fields to one replacement sender. */
  applySenderParametersToSender(sender: RTCRtpSender, config: WebRtcOptimizationConfig,
    recoveryLimit?: SenderRecoveryLimit): void {
    if (sender.track?.kind !== 'video') return;
    this.applyVideoSenderParams(sender, config, true, undefined, recoveryLimit);
  }

  /** Pin one replacement sender without manufacturing a peer-connection adapter. */
  pinSenderToHardwareCodecSender(
    sender: RTCRtpSender,
    config: WebRtcOptimizationConfig,
  ): Promise<SendCodecDecision<CodecCapability> | null> {
    if (sender.track?.kind !== 'video') return Promise.resolve(null);
    return this.serializeSenderParameters(sender, () =>
      this.senderCodecPin.pin(sender, config, encodeProfileProbe(hardwareProbeVideoFor(config))));
  }

  /**
   * Apply recovery / override encoding fields on all video senders.
   * Used by CongestionRecoveryManager.
   *
   * Bitrate and optional maxFramerate are the levers. Resolution is re-pinned on
   * every write so a recovery episode cannot move it. active is left true so a
   * sender the pin never owned cannot be parked off. setParameters rejections are
   * caught; synchronous parameter-access errors propagate.
   */
  applyVideoEncodingOverrides(
    rtcPc: RTCPeerConnection | null,
    overrides: { maxBitrateBps: number; maxFramerate?: number; scalabilityMode?: string },
  ): void {
    if (!rtcPc || typeof rtcPc.getSenders !== 'function') return;
    for (const sender of rtcPc.getSenders()) {
      if (sender.track?.kind !== 'video') continue;
      void this.serializeSenderParameters(sender, async () => {
        const params = sender.getParameters() as RTCRtpSendParameters;
        if (!params.encodings?.length) return;
        const existingCodec = (params.encodings[0] as { codec?: CodecCapability }).codec;
        params.encodings[0].maxBitrate = overrides.maxBitrateBps;
        // Frame rate stays at 60. Recovery used to write 45 or 30 here, and the climb
        // would not put it back while the sticky ceiling was lowered.
        // if (
        //   overrides.maxFramerate !== undefined &&
        //   Number.isFinite(overrides.maxFramerate) &&
        //   overrides.maxFramerate > 0
        // ) {
        //   params.encodings[0].maxFramerate = overrides.maxFramerate;
        // }
        params.encodings[0].maxFramerate = TARGET_FRAMERATE;
        params.encodings[0].scaleResolutionDownBy = 1;
        // A sender the pin has never owned must stay able to send. Do not write false.
        params.encodings[0].active = true;
        // Preserve a configured temporal-scaling mode across recovery cut writes: the
        // setParameters in this method would otherwise wipe scalabilityMode set at connect.
        if (
          overrides.scalabilityMode !== undefined &&
          (params.encodings[0] as { scalabilityMode?: string }).scalabilityMode !==
            overrides.scalabilityMode
        ) {
          (params.encodings[0] as { scalabilityMode?: string }).scalabilityMode =
            overrides.scalabilityMode;
        }
        delete (params.encodings[0] as { priority?: string }).priority;
        delete (params.encodings[0] as { networkPriority?: string }).networkPriority;
        if (existingCodec) {
          (params.encodings[0] as { codec?: CodecCapability }).codec = existingCodec;
        }
        await sender.setParameters(params);
      }).catch((err) => {
        console.warn('[WebRtcOptimizationManager] Failed to apply encoding overrides:', err);
      });
    }
  }

  private applyAudioSenderParams(
    sender: RTCRtpSender,
    config: WebRtcOptimizationConfig,
    allowRetry = true,
    skipRetryCheck?: () => boolean,
  ): void {
    const bitrate = config.audioMaxBitrate;
    if (!bitrate || !Number.isFinite(bitrate) || bitrate <= 0) return;

    const params = sender.getParameters() as RTCRtpSendParameters;
    if (!params.encodings || params.encodings.length === 0) {
      this.scheduleEncodingRetryIfNeeded(sender, config, 'audio', allowRetry, skipRetryCheck);
      return;
    }

    params.encodings[0].maxBitrate = bitrate;
    // Pacer priority only. networkPriority would change how GCC shares the link.
    (params.encodings[0] as { priority?: string }).priority = 'high';
    delete (params.encodings[0] as { networkPriority?: string }).networkPriority;

    sender.setParameters(params).catch((err) => {
      console.warn('[WebRtcOptimizationManager] Failed to set audio sender parameters:', err);
    });
  }

  private scheduleEncodingRetryIfNeeded(
    sender: RTCRtpSender,
    config: WebRtcOptimizationConfig,
    kind: 'video' | 'audio',
    allowRetry: boolean,
    skipRetryCheck?: () => boolean,
    recoveryLimit?: SenderRecoveryLimit,
  ): void {
    if (!allowRetry) {
      console.warn(`[WebRtcOptimizationManager] ${kind} sender encodings still unavailable after retry`);
      return;
    }
    if (this.pendingEncodingRetry.has(sender)) return;

    this.pendingEncodingRetry.add(sender);
    console.log(
      `[WebRtcOptimizationManager] ${kind} sender encodings unavailable; retrying in ${WebRtcOptimizationManager.ENCODING_RETRY_DELAY_MS}ms`,
    );

    const timer = setTimeout(() => {
      this.pendingEncodingRetry.delete(sender);
      this.pendingEncodingRetryTimers.delete(timer);
      if (skipRetryCheck?.()) {
        console.log(`[WebRtcOptimizationManager] ${kind} sender encoding retry skipped (recovery active)`);
        return;
      }
      try {
        if (kind === 'video') this.applyVideoSenderParams(sender, config, false, undefined, recoveryLimit);
        else this.applyAudioSenderParams(sender, config, false);
      } catch (error) {
        console.warn(`[WebRtcOptimizationManager] ${kind} sender encoding retry failed:`, error);
      }
    }, WebRtcOptimizationManager.ENCODING_RETRY_DELAY_MS);
    this.pendingEncodingRetryTimers.add(timer);
  }

  private applyReceiverLowLatencySettings(
    rtcPc: RTCPeerConnection,
    config: WebRtcOptimizationConfig,
  ): void {
    const videoTargetMs = config.jitterBufferTarget ?? 0;
    const audioTargetMs = config.audioJitterBufferTarget ?? videoTargetMs;
    const receivers = rtcPc.getReceivers();

    for (const receiver of receivers) {
      if (!receiver.track) continue;

      const targetMs = receiver.track.kind === 'audio' ? audioTargetMs : videoTargetMs;

      if ('jitterBufferTarget' in receiver) {
        try {
          (receiver as { jitterBufferTarget?: number }).jitterBufferTarget = targetMs;
        } catch (error) {
          console.warn('[WebRtcOptimizationManager] jitterBufferTarget rejected:', error);
        }
      }

      if ('playoutDelayHint' in receiver) {
        try {
          // playoutDelayHint is in SECONDS; jitterBufferTarget is in milliseconds.
          (receiver as { playoutDelayHint?: number }).playoutDelayHint = targetMs / 1000;
        } catch (error) {
          console.warn('[WebRtcOptimizationManager] playoutDelayHint rejected:', error);
        }
      }
    }
  }
}
